/* eslint-disable prettier/prettier */
import React, { useState, useEffect } from 'react';
import ReactLoader from 'react-loading';
import { useHistory } from 'react-router-dom';

import { ColorPalette } from 'theme/colors';

import AddExpense from 'components/AddExpenseBottomSheet';
import Header from 'components/AppBar';
import Card from 'components/Cards/CardTravel';
import EmptyList from 'components/EmptyList';
import Loader from 'components/Loader';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { TravelsWithExpensesResponse, ExpenseTravels, TravelsWithExpensesFilters } from 'models/expense';
import { getFormattedPrice } from 'utils/currency';
import { formatISODateToDDMMYYYY, htmlT } from 'utils/generic';

import {
  SubHeader,
  TitleContainer,
  ExpenseCard,
  Container,
  StyledList,
  Title,
  SubTitle,
  PeriodSelect,
  LoadingContainer,
} from './styles';

interface Props {
  travelsWithExpenses?: TravelsWithExpensesResponse;
  filters?: TravelsWithExpensesFilters;
  handleFilter: (filters: TravelsWithExpensesFilters, period: string) => void;
  initialPeriod?: string;
  loading: boolean;
  initializing: boolean;
}

const NoResults = () => {
  const { t } = useStoreTranslation();

  return (
    <EmptyList
      grey
      paddingMessage={54}
      title={t('travelWithExpensesList.empty.title')}
      description={t('travelWithExpensesList.empty.content')}
      image="/images/receipt-list-43-colored-48.svg"
      backgroundImage="/images/group-3.svg"
    />
  );
};

const NoResultsForFilter = ({ filtered }: { filtered?: boolean }) => {
  const { t } = useStoreTranslation();

  return (
    <EmptyList
      paddingMessage={54}
      title={t('travelWithExpensesList.empty.filtered.title')}
      description={t('travelWithExpensesList.empty.filtered.content')}
      image="/images/receipt-list-43-colored-48.svg"
    />
  );
};

const ExpenseTravelsList = ({
  travelsWithExpenses,
  loading,
  initializing,
  handleFilter,
  initialPeriod,
  filters,
}: Props) => {
  const { t } = useStoreTranslation();
  const history = useHistory();
  const [price, setPrice] = useState('');
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [selectedTravel, setSelectedTravel] = useState<ExpenseTravels>();

  const isFilterActive = filters && Object.keys(filters).length > 0;
  const showPeriodSelect = (travelsWithExpenses && travelsWithExpenses.quantityTravels >= 10) || isFilterActive;
  const hasResults = !!travelsWithExpenses?.travels?.length;

  useEffect(() => {
    if (!loading && travelsWithExpenses?.currencyCode) {
      const formattedPrice = (value: number | undefined) => {
        const { formatted } = getFormattedPrice({ value, currency: travelsWithExpenses?.currencyCode, t });
        return formatted;
      };
      setPrice(formattedPrice(travelsWithExpenses?.totalValue || 0));
    }
  }, [loading, t, travelsWithExpenses]);

  const handleAdd = () => {
    history.push('/expense', { id: selectedTravel?.id });
  };

  const handleImport = () => {
    history.push(`/expensesImport/${selectedTravel?.id}`, { id: selectedTravel?.id });
  };

  const handleClick = (travel: ExpenseTravels) => {
    if (travel.quantityExpenses === 0) {
      setSelectedTravel(travel);
      setShowBottomSheet(true);
    } else {
      history.push(`/expenses/${travel.id}`);
    }
  };

  return initializing ? (
    <Loader fullScreen />
  ) : (
    <>
      <Mobile>
        <Header goBack={() => history.push('/dashboard')}>{t('travelWithExpensesList.headerTitle')}</Header>
      </Mobile>

      <Desktop>
        <ScreenHeader title={t('travelWithExpensesList.headerTitle')} />
      </Desktop>

      {isFilterActive || hasResults ? (
        <Container>
          <SubHeader>
            <TitleContainer>
              <Title>{t('travelWithExpensesList.title')}</Title>
              <SubTitle>{t('travelWithExpensesList.subTitle')}</SubTitle>
            </TitleContainer>

            <ExpenseCard>
              <ExpenseCard.Info>
                <ExpenseCard.InfoTitle>{t('travelWithExpensesList.expensesCardInfo')}</ExpenseCard.InfoTitle>
                <ExpenseCard.Date>
                  {htmlT(t, 'travelWithExpensesList.date', {
                    date: formatISODateToDDMMYYYY(travelsWithExpenses?.initialDate),
                  })}
                </ExpenseCard.Date>
              </ExpenseCard.Info>
              <ExpenseCard.Price>
                <ExpenseCard.Icon src="/images/receipt-list-42.svg" alt="" />
                {price}
              </ExpenseCard.Price>
            </ExpenseCard>
          </SubHeader>
          {showPeriodSelect && (
            <PeriodSelect
              handleChangeDate={(start, end, period) =>
                handleFilter({ FilterStartDate: start, FilterEndDate: end }, period)
              }
              initialValue={initialPeriod}
              initialDates={{ start: filters?.FilterStartDate || '', end: filters?.FilterEndDate || '' }}
            />
          )}
          {loading ? (
            <LoadingContainer>
              <ReactLoader type="spin" color={ColorPalette.HEX.Gainsboro} width={60} height={60} />
            </LoadingContainer>
          ) : hasResults ? (
            <StyledList>
              {(travelsWithExpenses?.travels || []).map((travel) => (
                <Card key={travel.id} travel={travel} onClick={() => handleClick(travel)} onTravelCancel={() => { }} />
              ))}
            </StyledList>
          ) : (
            <NoResultsForFilter />
          )}
        </Container>
      ) : (
        <NoResults />
      )}
      <AddExpense
        show={showBottomSheet}
        onAdd={handleAdd}
        onImport={handleImport}
        onClose={() => setShowBottomSheet(false)}
      />
    </>
  );
};

export default ExpenseTravelsList;
